// File: src/pages/InvoiceReviewPage.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/axios";
import { useAuth } from "@/context/AuthProvider";
import { OPERATIONAL_CONTEXT_EVENT } from "@/lib/procurementOperationalContext";
import { readInvoiceReview, canUpdateInvoiceReview, updateInvoiceReview } from "@/lib/invoiceReviewAccess";
import {
  Loader,
  AlertTriangle,
  RefreshCw,
  FileText,
  Download,
  Eye,
  ArrowLeft,
  CheckCircle,
  ClipboardCheck,
  Activity,
  Receipt,
  Link2,
} from "lucide-react";
import dayjs from "dayjs";

const currency = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

function toPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function firstNumber(...values) {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function firstNonZeroNumber(...values) {
  let firstFinite = null;

  for (const value of values) {
    const n = Number(value);
    if (!Number.isFinite(n)) continue;

    if (firstFinite === null) firstFinite = n;
    if (Math.abs(n) > 0.0001) return n;
  }

  return firstFinite ?? 0;
}

function isFinalRejectedAction(action) {
  const safe = String(action || "").trim().toLowerCase();
  return ["ignore", "credit", "credited", "final", "writeoff", "write-off"].includes(
    safe
  );
}

function getLineTotal(line) {
  return Number(line.total_line_value ?? line.total ?? 0);
}

function getApiBaseUrl() {
  const base = api?.defaults?.baseURL || "";
  return String(base).replace(/\/$/, "");
}

function buildFileUrl(filePath) {
  if (!filePath) return null;
  const raw = String(filePath);
  if (/^https?:\/\//i.test(raw)) return raw;
  const normalized = raw.startsWith("/") ? raw : `/${raw}`;
  return `${getApiBaseUrl()}${normalized}`;
}

function StatusBadge({ status }) {
  const safe = String(status || "review_required").toLowerCase();

  const className =
    safe === "matched"
      ? "border-green-400/40 bg-green-500/10 text-green-300"
      : safe === "reviewed"
        ? "border-blue-400/40 bg-blue-500/10 text-blue-300"
        : safe === "approved"
          ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300"
          : safe === "rejected"
            ? "border-red-400/40 bg-red-500/10 text-red-300"
            : "border-amber-400/40 bg-amber-500/10 text-amber-300";

  return (
    <span
      className={`rounded-full border px-3 py-1 text-xs font-semibold ${className}`}
    >
      {safe.replaceAll("_", " ").toUpperCase()}
    </span>
  );
}

export default function InvoiceReviewPage() {
  const {user}=useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const invoiceId = useMemo(() => {
    const fromPath = params.invoiceId || params.invoice_id || params.id;
    const fromQuery =
      searchParams.get("invoiceId") ||
      searchParams.get("invoice_id") ||
      searchParams.get("id");

    return toPositiveNumber(fromQuery || fromPath);
  }, [params, searchParams]);

  const [loading, setLoading] = useState(false);
  const [loadingPicker, setLoadingPicker] = useState(false);
  const [loadingError, setLoadingError] = useState(null);
  const [pendingInvoices, setPendingInvoices] = useState([]);

  const identity=JSON.stringify([user?.id,user?.company_id,user?.unit_id,user?.role,user?.is_platform_admin,user?.permissions,invoiceId]);
  const identityRef=useRef(identity);identityRef.current=identity;
  const actorRef=useRef(user);actorRef.current=user;
  const activeRequest=useRef(null),sequence=useRef(0);
  const [hubState,setHubState]=useState({key:'',data:null});
  const hub=hubState.key===identity?hubState.data:null;
  const lineItems=Array.isArray(hub?.line_items)?hub.line_items:[];
  const canUpdate=canUpdateInvoiceReview(hub,invoiceId,user);
  const [matchFilter, setMatchFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  const downloadSystemInvoice = async () => {
    if (!invoiceId || downloadingInvoice) return;
    setDownloadingInvoice(true);
    setDownloadError("");
    try {
      const response = await api.get(
        `/invoice/view/${invoiceId}`,
        { params: { download: "1" }, responseType: "blob",
          headers: { Accept: "application/pdf" } }
      );
      const blob = response.data;
      if (!(blob instanceof Blob) ||
          (await blob.slice(0, 5).text()) !== "%PDF-") {
        throw new Error("The server did not return a PDF. Please try again.");
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `system-invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      try {
        link.click();
      } finally {
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (error) {
      let payload = error?.response?.data;
      if (payload instanceof Blob) {
        try { payload = JSON.parse(await payload.text()); }
        catch { payload = null; }
      }
      setDownloadError(
        payload?.error || payload?.message ||
        error.message || "Unable to download the system invoice."
      );
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const loadHub = useCallback(() => {
    if (!invoiceId) return;
    const serial=++sequence.current;
    activeRequest.current?.abort();
    const controller=new AbortController();activeRequest.current=controller;
    setLoading(true);
    setLoadingError(null);
    setHubState({key:identity,data:null});
    return readInvoiceReview(api,invoiceId,actorRef.current,{signal:controller.signal})
      .then((payload) => {
        if(controller.signal.aborted||serial!==sequence.current||identityRef.current!==identity)return;
        setHubState({key:identity,data:payload});
      })
      .catch((err) => {
        if(controller.signal.aborted||serial!==sequence.current||identityRef.current!==identity)return;
        console.error("[InvoiceReview] Hub API error:", err);
        const status = err?.response?.status;
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err.message ||
          "Failed to load invoice intelligence hub.";

        if (status === 403) navigate("/forbidden");
        else setLoadingError(msg);
      })
      .finally(() => {if(!controller.signal.aborted&&serial===sequence.current)setLoading(false);});
  },[identity,invoiceId,navigate]);

  useEffect(() => {
    if (invoiceId) return;

    setLoadingPicker(true);
    setLoadingError(null);

    api
      .get("/solo-mdsh-api/procurement/invoices/pending")
      .then((res) => {
        setPendingInvoices(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("[InvoiceReview] Pending invoice picker error:", err);
        setLoadingError(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            err.message ||
            "Failed to load invoices for review."
        );
      })
      .finally(() => setLoadingPicker(false));
  }, [invoiceId]);

  useEffect(() => {
    loadHub();
    const changed=()=>loadHub();window.addEventListener(OPERATIONAL_CONTEXT_EVENT,changed);
    return ()=>{sequence.current++;activeRequest.current?.abort();window.removeEventListener(OPERATIONAL_CONTEXT_EVENT,changed);};
  }, [loadHub]);

  const filteredLineItems = useMemo(() => {
    if (!Array.isArray(lineItems) || lineItems.length === 0) return [];
    if (matchFilter === "all") return lineItems;

    return lineItems.filter(
      (line) =>
        String(line.match_status || "unreviewed").toLowerCase() === matchFilter
    );
  }, [lineItems, matchFilter]);

  const totalLinesAmount = useMemo(
    () => filteredLineItems.reduce((sum, line) => sum + getLineTotal(line), 0),
    [filteredLineItems]
  );

  const uploadedInvoiceUrl = buildFileUrl(hub?.file_path || hub?.file_url);
  const systemInvoiceUrl = invoiceId
    ? `/invoice/detail/${invoiceId}`
    : null;

  const poFinance = hub?.po_finance || {};
  const hubSummary = hub?.hub || {};
  const rejectionBreakdown = Array.isArray(hub?.rejection_breakdown)
    ? hub.rejection_breakdown
    : [];

  const invoiceGrossTotal = firstNumber(
    hubSummary.invoice_gross_total,
    hubSummary.invoice_total,
    hub?.total_amount
  );

  const poNetTotal = firstNumber(
    poFinance.po_net_total,
    poFinance.po_subtotal,
    poFinance.po_total_net
  );

  const poTaxTotal = firstNumber(
    poFinance.po_tax_total,
    poFinance.po_vat_total,
    poFinance.po_total_tax
  );

  const poGrossTotal = firstNumber(
    poFinance.po_gross_total,
    poFinance.po_total_value,
    poFinance.po_total
  );

  const finalRejectedGrossFromRows = rejectionBreakdown
    .filter((row) => isFinalRejectedAction(row.rejection_action))
    .reduce(
      (sum, row) =>
        sum +
        firstNonZeroNumber(
          row.estimated_gross_value,
          row.gross_value,
          row.total_gross,
          row.estimated_total_value,
          row.estimated_value
        ),
      0
    );

  const finalRejectedGrossTotal = firstNonZeroNumber(
    poFinance.final_rejected_gross_total,
    poFinance.final_rejected_total_value,
    poFinance.ignore_rejected_gross_total,
    poFinance.ignore_rejected_total_value,
    poFinance.ignored_rejected_gross_total,
    poFinance.ignored_rejected_total_value,
    poFinance.credit_rejected_gross_total,
    poFinance.credit_rejected_total_value,
    poFinance.credit_rejected_total,
    finalRejectedGrossFromRows
  );

  const rejectedGrossTotal = firstNonZeroNumber(
    poFinance.rejected_gross_total,
    poFinance.rejected_total_value,
    poFinance.rejected_total,
    rejectionBreakdown.reduce(
      (sum, row) =>
        sum +
        firstNonZeroNumber(
          row.estimated_gross_value,
          row.gross_value,
          row.total_gross,
          row.estimated_total_value,
          row.estimated_value
        ),
      0
    )
  );

  const receivedGrossTotal = firstNumber(
    poFinance.received_gross_total,
    poFinance.received_total_value,
    poFinance.received_total
  );

  const expectedPayableNet = firstNumber(
    hubSummary.expected_payable_net,
    poFinance.expected_payable_net,
    poFinance.expected_payable_subtotal
  );

  const expectedPayableTaxFromApi = firstNumber(
    hubSummary.expected_payable_tax,
    poFinance.expected_payable_tax,
    poFinance.expected_payable_vat
  );

  const recalculatedExpectedGrossPayable =
    poGrossTotal > 0
      ? Math.max(poGrossTotal - finalRejectedGrossTotal, 0)
      : firstNumber(hubSummary.expected_payable_total, poFinance.expected_payable_total);

  const recalculatedExpectedVat =
    expectedPayableTaxFromApi > 0
      ? expectedPayableTaxFromApi
      : Math.max(recalculatedExpectedGrossPayable - expectedPayableNet, 0);

  const recalculatedTrueVariance = invoiceGrossTotal - recalculatedExpectedGrossPayable;

  const updateStatus = async (status) => {
    if (!invoiceId || !canUpdate || actionLoading) return;

    setActionLoading(true);
    try {
      await updateInvoiceReview(api,hub,invoiceId,user,status);
      await loadHub();
    } catch (err) {
      console.error("[InvoiceReview] Status update failed:", err);
      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to update invoice status."
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (!invoiceId) {
    return (
      <div className="p-6 text-white">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">🧠 Invoice Intelligence Hub</h1>
            <p className="text-sm text-gray-400 mt-1">
              Select an invoice to review upload, system invoice, PO matching,
              rejection impact, tax, and activity.
            </p>
          </div>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-sm hover:bg-white/10"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {loadingPicker && (
          <div className="flex items-center gap-2 text-gray-300 text-sm">
            <Loader className="w-4 h-4 animate-spin" />
            Loading invoices...
          </div>
        )}

        {loadingError && (
          <div className="text-sm text-red-400 flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4" />
            {loadingError}
          </div>
        )}

        {!loadingPicker && !loadingError && pendingInvoices.length === 0 && (
          <p className="text-sm text-yellow-300">
            No pending invoices found for review.
          </p>
        )}

        {pendingInvoices.length > 0 && (
          <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
            <table className="min-w-full text-sm">
              <thead className="bg-[#141414]">
                <tr>
                  <th className="px-3 py-2 text-left">Invoice #</th>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-left">PO</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Uploaded</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {pendingInvoices.map((inv, idx) => (
                  <tr
                    key={inv.invoice_id || idx}
                    className={idx % 2 === 0 ? "bg-[#101010]" : "bg-[#0b0b0b]"}
                  >
                    <td className="px-3 py-2 font-mono">
                      {inv.invoice_number || `#${inv.invoice_id}`}
                    </td>
                    <td className="px-3 py-2">
                      {inv.vendor_name || "Unknown vendor"}
                    </td>
                    <td className="px-3 py-2">
                      {inv.po_number || inv.po_id || "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {currency(inv.amount ?? inv.amount_due)}
                    </td>
                    <td className="px-3 py-2">
                      {inv.uploaded_at
                        ? dayjs(inv.uploaded_at).format("YYYY-MM-DD HH:mm")
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => navigate(`/invoice/review/${inv.invoice_id}`)}
                        className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-semibold hover:bg-blue-500"
                      >
                        Open Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 text-white">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate("/invoice/review")}
            className="mb-3 inline-flex items-center gap-2 text-xs text-gray-300 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to invoice review list
          </button>

          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold">🧠 Invoice Intelligence Hub</h1>
            {hub?.hub?.status && <StatusBadge status={hub.hub.status} />}
          </div>

          <div className="text-sm text-gray-400 mt-1">
            Invoice ID:{" "}
            <span className="text-amber-300 font-mono">{invoiceId}</span>
            {hub?.invoice_number && (
              <>
                {" "}
                | Invoice #:{" "}
                <span className="text-gray-200 font-mono">
                  {hub.invoice_number}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <button
            disabled={actionLoading || !canUpdate}
            onClick={() => updateStatus("matched")}
            className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-3 py-2 text-xs font-semibold hover:bg-green-500 disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4" />
            Mark Matched
          </button>

          <button
            disabled={actionLoading || !canUpdate}
            onClick={() => updateStatus("reviewed")}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold hover:bg-blue-500 disabled:opacity-50"
          >
            <ClipboardCheck className="w-4 h-4" />
            Mark Reviewed
          </button>

          <button
            disabled={actionLoading || !canUpdate}
            onClick={() => updateStatus("review_required")}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold hover:bg-amber-500 disabled:opacity-50"
          >
            <AlertTriangle className="w-4 h-4" />
            Needs Review
          </button>
          {hub && !canUpdate && <p className="w-full text-xs text-slate-400">{hub.review_access?.status_change_reason==='financially_locked'?'This invoice is locked. Use the approval and payment workspaces for financial actions.':'You have viewing access. Invoice review changes require approval permission.'}</p>}
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-gray-300 text-sm mb-3">
          <Loader className="w-4 h-4 animate-spin" />
          Loading invoice intelligence hub...
        </div>
      )}

      {loadingError && (
        <div className="text-sm text-red-400 flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4" />
          {loadingError}
        </div>
      )}

      {hub && (
        <>
          <div className="grid md:grid-cols-5 gap-4 mb-6">
            <div className="rounded-xl border border-white/10 bg-[#101010] p-4">
              <div className="text-xs text-gray-400">Invoice Gross</div>
              <div className="text-xl font-bold">
                {currency(invoiceGrossTotal)}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#101010] p-4">
              <div className="text-xs text-gray-400">Expected Net</div>
              <div className="text-xl font-bold">
                {currency(expectedPayableNet)}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#101010] p-4">
              <div className="text-xs text-gray-400">Expected VAT</div>
              <div className="text-xl font-bold text-cyan-300">
                {currency(recalculatedExpectedVat)}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#101010] p-4">
              <div className="text-xs text-gray-400">Expected Gross</div>
              <div className="text-xl font-bold text-green-300">
                {currency(recalculatedExpectedGrossPayable)}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#101010] p-4">
              <div className="text-xs text-gray-400">True Variance</div>
              <div
                className={`text-xl font-bold ${
                  Math.abs(toNumber(recalculatedTrueVariance)) <= 1
                    ? "text-green-300"
                    : "text-red-300"
                }`}
              >
                {currency(recalculatedTrueVariance)}
              </div>
            </div>
          </div>

          {hub.hub?.summary && (
            <div className="rounded-xl border border-white/10 bg-black/30 p-4 mb-6 text-sm text-gray-200">
              {hub.hub.summary}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="rounded-xl border border-white/10 bg-[#101010] p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-blue-300" />
                <h2 className="font-semibold">Uploaded Vendor Invoice</h2>
              </div>

              {uploadedInvoiceUrl ? (
                <div className="flex gap-2 flex-wrap">
                  <a
                    href={uploadedInvoiceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold hover:bg-blue-500"
                  >
                    <Eye className="w-4 h-4" />
                    View Uploaded
                  </a>

                  <a
                    href={uploadedInvoiceUrl}
                    download
                    className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold hover:bg-white/10"
                  >
                    <Download className="w-4 h-4" />
                    Download Uploaded
                  </a>
                </div>
              ) : (
                <p className="text-sm text-yellow-300">
                  No uploaded invoice file is linked.
                </p>
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-[#101010] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Receipt className="w-4 h-4 text-purple-300" />
                <h2 className="font-semibold">System Invoice View</h2>
              </div>

              {systemInvoiceUrl ? (
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => navigate(systemInvoiceUrl)}
                    className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-xs font-semibold hover:bg-purple-500"
                  >
                    <Eye className="w-4 h-4" />
                    View System Invoice
                  </button>

                  <button
                    type="button"
                    onClick={downloadSystemInvoice}
                    disabled={downloadingInvoice}
                    aria-busy={downloadingInvoice}
                    className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold hover:bg-white/10"
                  >
                    <Download className="w-4 h-4" />
                    {downloadingInvoice ? "Downloading..." : "Download System Invoice"}
                  </button>
                  {downloadError && (
                    <p role="alert" className="w-full text-sm text-red-300">
                      {downloadError}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-yellow-300">
                  No system invoice view is available.
                </p>
              )}
            </div>
          </div>

          {hub.po_finance && (
            <div className="rounded-xl border border-white/10 bg-[#101010] p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4 text-green-300" />
                <h2 className="font-semibold">
                  PO Receiving, Rejection & Tax Impact
                </h2>
              </div>

              <div className="grid md:grid-cols-6 gap-3 text-sm">
                <div>
                  <div className="text-gray-400 text-xs">PO Net</div>
                  <div className="font-semibold">{currency(poNetTotal)}</div>
                </div>

                <div>
                  <div className="text-gray-400 text-xs">PO VAT</div>
                  <div className="font-semibold text-cyan-300">
                    {currency(poTaxTotal)}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400 text-xs">PO Gross</div>
                  <div className="font-semibold">{currency(poGrossTotal)}</div>
                </div>

                <div>
                  <div className="text-gray-400 text-xs">
                    Final / Ignore Rejected Gross
                  </div>
                  <div className="font-semibold text-red-300">
                    {currency(finalRejectedGrossTotal)}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400 text-xs">Expected VAT</div>
                  <div className="font-semibold text-cyan-300">
                    {currency(recalculatedExpectedVat)}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400 text-xs">
                    Expected Gross Payable
                  </div>
                  <div className="font-semibold text-green-300">
                    {currency(recalculatedExpectedGrossPayable)}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3 text-sm mt-4 border-t border-white/10 pt-4">
                <div>
                  <div className="text-gray-400 text-xs">Received Gross</div>
                  <div className="font-semibold">
                    {currency(receivedGrossTotal)}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400 text-xs">Rejected Gross</div>
                  <div className="font-semibold">
                    {currency(rejectedGrossTotal)}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400 text-xs">Variance Meaning</div>
                  <div className="text-xs text-gray-300">
                    Invoice gross minus recalculated expected gross payable
                    after final / ignore rejection adjustments.
                  </div>
                </div>
              </div>
            </div>
          )}

          {Array.isArray(hub.rejection_breakdown) &&
            hub.rejection_breakdown.length > 0 && (
              <div className="rounded-xl border border-white/10 bg-[#101010] p-4 mb-6">
                <h2 className="font-semibold mb-3">
                  Rejected / Backordered Items
                </h2>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-black/40">
                      <tr>
                        <th className="px-2 py-2 text-left">Item</th>
                        <th className="px-2 py-2 text-right">Qty</th>
                        <th className="px-2 py-2 text-left">Action</th>
                        <th className="px-2 py-2 text-left">Reason</th>
                        <th className="px-2 py-2 text-right">Est. Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hub.rejection_breakdown.map((row) => (
                        <tr
                          key={row.rejection_id}
                          className="border-t border-white/5"
                        >
                          <td className="px-2 py-2">
                            {row.item_code} — {row.item_name}
                          </td>
                          <td className="px-2 py-2 text-right">
                            {row.quantity_rejected}
                          </td>
                          <td className="px-2 py-2">
                            {row.rejection_action}
                            {row.is_backordered ? " / Backordered" : ""}
                          </td>
                          <td className="px-2 py-2">
                            {row.rejection_reason || "—"}
                          </td>
                          <td className="px-2 py-2 text-right">
                            {currency(
                              firstNonZeroNumber(
                                row.estimated_gross_value,
                                row.gross_value,
                                row.total_gross,
                                row.estimated_total_value,
                                row.estimated_value
                              )
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </>
      )}

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-semibold">Invoice Lines</h2>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-300">Line Filter</span>
          <select
            value={matchFilter}
            onChange={(e) => setMatchFilter(e.target.value)}
            className="bg-[#111] border border-white/20 rounded px-3 py-1 text-sm"
          >
            <option value="all">All</option>
            <option value="matched">Matched</option>
            <option value="unmatched">Unmatched</option>
            <option value="unreviewed">Unreviewed</option>
          </select>
        </div>
      </div>

      {filteredLineItems.length === 0 ? (
        <p className="text-sm text-yellow-300 mt-4">
          No invoice lines found for this invoice and filter.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
          <table className="min-w-full text-sm">
            <thead className="bg-[#141414] sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 border-b border-white/10 text-left">
                  Code
                </th>
                <th className="px-2 py-2 border-b border-white/10 text-left">
                  Description
                </th>
                <th className="px-2 py-2 border-b border-white/10 text-right">
                  Qty
                </th>
                <th className="px-2 py-2 border-b border-white/10 text-right">
                  Unit Price
                </th>
                <th className="px-2 py-2 border-b border-white/10 text-right">
                  Line Total
                </th>
                <th className="px-2 py-2 border-b border-white/10 text-left">
                  Match Status
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredLineItems.map((line, idx) => {
                const status = String(line.match_status || "unreviewed").toLowerCase();

                return (
                  <tr
                    key={
                      line.id ||
                      line.extracted_id ||
                      `${line.invoice_id || invoiceId}-${idx}`
                    }
                    className={idx % 2 === 0 ? "bg-[#101010]" : "bg-[#0b0b0b]"}
                  >
                    <td className="px-2 py-2 font-mono text-xs">
                      {line.product_code || line.item_code || "—"}
                    </td>
                    <td className="px-2 py-2">
                      {line.description || line.product_description || "—"}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {Number(line.quantity || 0)}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {currency(line.unit_price || 0)}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {currency(getLineTotal(line))}
                    </td>
                    <td className="px-2 py-2">
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              <tr className="bg-black/60">
                <td colSpan={4} className="px-2 py-2 text-right font-semibold">
                  Total filtered:
                </td>
                <td className="px-2 py-2 text-right font-semibold">
                  {currency(totalLinesAmount)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {Array.isArray(hub?.activity) && hub.activity.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-[#101010] p-4 mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-cyan-300" />
            <h2 className="font-semibold">Invoice Activity Log</h2>
          </div>

          <div className="space-y-3">
            {hub.activity.map((row, idx) => (
              <div key={`${row.type}-${idx}`} className="border-l border-white/20 pl-3">
                <div className="text-sm font-semibold">{row.title}</div>
                <div className="text-xs text-gray-400">
                  {row.at ? dayjs(row.at).format("YYYY-MM-DD HH:mm") : "—"}
                  {row.actor ? ` · ${row.actor}` : ""}
                </div>
                {row.note && (
                  <div className="text-xs text-gray-300 mt-1">{row.note}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
